import { inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { map, mergeMap, catchError, switchMap, exhaustMap, withLatestFrom } from 'rxjs/operators'
import { of } from 'rxjs'
import {
  loadBusinesses,
  loadBusinessesFailure,
  loadBusinessesSuccess,
  blockBusiness,
  blockBusinessSuccess,
  blockBusinessFailure,
  unblockBusiness,
  unblockBusinessSuccess,
  unblockBusinessFailure,
  deleteBusiness,
  deleteBusinessSuccess,
  deleteBusinessFailure,
  createBusiness,
  createBusinessSuccess,
  createBusinessFailure,
  loadBusinessById,
  loadBusinessByIdSuccess,
  loadBusinessByIdFailure,
  updateBusiness,
  updateBusinessSuccess,
  updateBusinessFailure,
  assignOwnerSuccess,
  assignOwner,
  assignOwnerFailure
} from './business.actions'
import { BusinessService } from '../../services/business.service'
import { ToastrService } from 'ngx-toastr'
import { Store } from '@ngrx/store'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { selectUser } from '../auth/auth.selectors'
import { updateAuthUser } from '../auth/auth.actions'

// export const loadBusinesses$ = createEffect(
//   (actions$ = inject(Actions), busienssService = inject(BusinessService)) =>
//     actions$.pipe(
//       ofType(loadBusinesses),
//       mergeMap(() =>
//         busienssService.getBusinesses().pipe(
//           map(res =>
//             loadBusinessesSuccess({ totalCount: res.totalCount, businesses: res.businesses })
//           ),
//           catchError(error => of(loadBusinessesFailure({ error: error.message })))
//         )
//       )
//     ),
//   { functional: true } // ✅ Required for functional effects
// )

export const loadBusinesses$ = createEffect(
  (actions$ = inject(Actions), businessService = inject(BusinessService)) =>
    actions$.pipe(
      ofType(loadBusinesses),
      switchMap(
        (
          action // Use action.filter here
        ) =>
          businessService.getBusinesses(action.params, action.page, action.limit).pipe(
            map(res =>
              loadBusinessesSuccess({
                totalCount: res.totalCount,
                businesses: res.businesses,
                activeCount: res.activeCount,
                inactiveCount: res.inactiveCount,
                totalOffices: res.totalOffices,
                totalDepartments: res.totalDepartments,
                totalDesignations: res.totalDesignations,
                totalRowCount: res.totalRowCount,
                // ✅ PASS THROUGH FROM ACTION
                context: action.context ?? 'list'
              })
            ),
            catchError(error => of(loadBusinessesFailure({ error: error.message })))
          )
      )
    ),
  { functional: true }
)

// export const blockBusiness$ = createEffect(
//   (actions$ = inject(Actions), busienssService = inject(BusinessService)) =>
//     actions$.pipe(
//       ofType(blockBusiness),
//       mergeMap(
//         (
//           action // Define action here
//         ) =>
//           busienssService.blockBusiness(action.id, action.disabled).pipe(
//             map(() => blockBusinessSuccess({ id: action.id })),
//             catchError(error => of(blockBusinessFailure({ error })))
//           )
//       )
//     ),
//   { functional: true } // ✅ Required for functional effects
// )

export const blockBusiness$ = createEffect(
  (
    actions$ = inject(Actions),
    businessService = inject(BusinessService),
    store = inject(Store),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(blockBusiness),
      exhaustMap(action =>
        businessService.blockBusiness(action.id, action.disabled).pipe(
          map(res => {
            // const actionToDispatch =
            // console.log('Dispatching blockBusinessSuccess:', { business: res })
            // store.dispatch(actionToDispatch) // Dispatch the action
            //dispatch block  business success action
            if (action.disabled === true) {
              // Fixed typo here
              toastr.success('Business inactive successfully!')
            } else {
              toastr.success('Business active successfully!')
            }
            // store.dispatch(loadBusinesses({ params: null, page: 1, limit: 10 }))
            return blockBusinessSuccess({ business: res })
            // return blockBusinessSuccess({ business: res })
            // return blockBusinessSuccess({ id: action.id })
          }),
          catchError(error => {
            toastr.error('Failed to block business: ' + error.message)
            return of(blockBusinessFailure({ error }))
          })
        )
      )
    ),
  { functional: true }
)

export const unblockBusiness$ = createEffect(
  (actions$ = inject(Actions), busienssService = inject(BusinessService)) =>
    actions$.pipe(
      ofType(unblockBusiness),
      exhaustMap(action =>
        busienssService.unblockBusiness(action.id).pipe(
          map(() => unblockBusinessSuccess({ id: action.id })),
          catchError(error => of(unblockBusinessFailure({ error })))
        )
      )
    ),
  { functional: true } // ✅ Required for functional effects
)

export const deleteBusiness$ = createEffect(
  (
    actions$ = inject(Actions),
    businessService = inject(BusinessService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(deleteBusiness),
      exhaustMap(action =>
        businessService.deleteBusiness(action.id).pipe(
          map(() => {
            toastr.success('Business deleted successfully!')
            return deleteBusinessSuccess({ id: action.id })
          }),
          catchError(error => {
            toastr.error('Failed to delete business: ' + error.message)
            return of(deleteBusinessFailure({ error }))
          })
        )
      )
    ),
  { functional: true }
)

export const createBusiness$ = createEffect(
  (
    actions$ = inject(Actions),
    businessService = inject(BusinessService),
    toastr = inject(ToastrService), // Inject the ToastrService
    store = inject(Store),
    modalService = inject(NgbModal)
  ) =>
    actions$.pipe(
      ofType(createBusiness),
      exhaustMap(({ businessName, picture, website, userId, parentBusinessId }) =>
        businessService
          .createBusiness({ businessName, picture, website, userId, parentBusinessId })
          .pipe(
            map(created => {
              // Dispatch the success action
              const successAction = createBusinessSuccess({ business: created })

              // Show a success message using Toastr
              toastr.success('Business created successfully!', 'Success')
              // store.dispatch(loadBusinesses({ params: null, page: 1, limit: 10 }))
              modalService.dismissAll()
              return successAction
            }),
            catchError(error => {
              // Optionally, show an error message
              toastr.error(error.error.message, 'Error')
              return of(createBusinessFailure({ error }))
            })
          )
      )
    ),
  { functional: true, dispatch: true }
)

export const loadBusinessById$ = createEffect(
  (actions$ = inject(Actions), businessService = inject(BusinessService)) =>
    actions$.pipe(
      ofType(loadBusinessById),
      switchMap(({ id }) =>
        businessService.getBusinessById(id).pipe(
          map(business => loadBusinessByIdSuccess({ business })),
          catchError(error => of(loadBusinessByIdFailure({ error: error.message })))
        )
      )
    ),
  { functional: true }
)

export const updateBusiness$ = createEffect(
  (
    actions$ = inject(Actions),
    businessService = inject(BusinessService),
    toastr = inject(ToastrService),
    store = inject(Store),
    modalService = inject(NgbModal)
  ) =>
    actions$.pipe(
      ofType(updateBusiness),
      withLatestFrom(store.select(selectUser)), // ✅ get logged-in user
      // exhaustMap(([action, currentUser]) =>
      exhaustMap(
        ([
          {
            id,
            businessName,
            picture,
            website,
            address,
            cityId,
            zipCode,
            referenceIdFacebook,
            referenceIdInstagram,
            referenceIdLinkedIn,
            referenceIdTwitter,
            referenceIdWhatsapp,
            empidprefix,
            empidprefixStart,
            contractoridprefix,
            contractoridprefixStart,
            traineeidprefix,
            traineeidprefixStart,
            currencyId,
            incorporationNo,
            incorporationDate,
            PAN,
            GST,
            userId,
            isAutoClockInOut,
            isMobileApp,
            latitude,
            longitude
          },
          currentUser
        ]) =>
          businessService
            .updateBusiness({
              id,
              businessName,
              picture,
              website,
              address,
              cityId,
              zipCode,
              referenceIdFacebook,
              referenceIdInstagram,
              referenceIdLinkedIn,
              referenceIdTwitter,
              referenceIdWhatsapp,
              empidprefix,
              empidprefixStart,
              contractoridprefix,
              contractoridprefixStart,
              traineeidprefix,
              traineeidprefixStart,
              currencyId,
              incorporationNo,
              incorporationDate,
              PAN,
              GST,
              userId,
              latitude,
              longitude,
              isAutoClockInOut,
              isMobileApp
            })
            .pipe(
              map(updatedBusiness => {
                // ✅ If the updated business is the same as the logged-in user's business
                if (updatedBusiness.id === currentUser?.businessId) {
                  const updatedUser = {
                    ...currentUser,
                    business: updatedBusiness
                  }
                  store.dispatch(updateAuthUser({ user: updatedUser }))
                  // Persist to localStorage so page reload keeps it
                  localStorage.setItem('user', JSON.stringify(updatedUser))
                }
                toastr.success('Business updated successfully!', 'Success')

                // Dispatch loadBusinessById after the update is successful
                // store.dispatch(loadBusinessById({ id }))
                // modalService.dismissAll()

                // Dispatch success action to update the store
                return updateBusinessSuccess({ business: updatedBusiness })
              }),
              catchError(error => {
                toastr.error(error.error.message, 'Error') // Show error message
                return of(updateBusinessFailure({ error }))
              })
            )
      )
    ),
  { functional: true }
)

export const assignOwner$ = createEffect(
  (
    actions$ = inject(Actions),
    businessService = inject(BusinessService),
    toastr = inject(ToastrService)
    // store = inject(Store)
  ) =>
    actions$.pipe(
      ofType(assignOwner),
      exhaustMap(({ id, userId }) =>
        businessService
          .assignOwner({
            id,
            userId
          })
          .pipe(
            map(assignOwner => {
              toastr.success('Owner assigned successfully!', 'Success')

              // Dispatch loadBusinessById after the update is successful
              // store.dispatch(loadBusinessById({ id }))

              // Dispatch success action to update the store
              return assignOwnerSuccess({ business: assignOwner })
            }),
            catchError(error => {
              toastr.error('Failed to assing owner', 'Error') // Show error message
              return of(assignOwnerFailure({ error }))
            })
          )
      )
    ),
  { functional: true }
)

export const businessEffects = {
  loadBusinesses$,
  unblockBusiness$,
  blockBusiness$,
  deleteBusiness$,
  createBusiness$,
  loadBusinessById$,
  updateBusiness$,
  assignOwner$
}
